import React from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import SettingsFittings from './SettingsFittings';
import SettingsRentals from './SettingsRentals';

// Online Booking — extracted from Settings.jsx on 17 Sep 2026. The markup is
// unchanged; every value it needs comes from the Settings context rather than
// from props, so this move could not alter behaviour.
// Appointment lengths for a package marked Appointment (fittings, 3 Oct).
const APPT_LENGTHS = [30, 45, 60, 90, 120, 150, 180];

export default function SettingsBooking() {
  const { T, allStatuses, bookingBase, bookingItemFilter, bookingItems, copiedLinkId, isDark, loadBookingItems, loadingBookingItems, setBooking, setBookingItemFilter, setBookingItems, setCopiedLinkId, settings } = useSettings();
  return (
    <>
      {/* ── Online Booking ── */}
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Online Booking</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Let customers book service online. Choose which service packages appear on the booking page, set daily capacity, and control which days accept drop-offs.
        </div>

        {/* Enable toggle */}
        <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, cursor:'pointer' }}>
          <input type="checkbox" checked={!!(settings.booking || {}).enabled}
            onChange={e => setBooking({ enabled: e.target.checked })}
            style={{ accentColor:'#F4622A', width:16, height:16 }} />
          <span style={{ fontSize:14, fontWeight:600, color:T.text }}>Enable online booking</span>
          <span style={{ fontSize:12, color:(settings.booking || {}).enabled ? '#2ecc71' : T.textSub }}>
            {(settings.booking || {}).enabled ? 'Live — booking page is public' : 'Off — booking page returns 404'}
          </span>
        </label>

        {/* Payment collection */}
        <div style={{ marginBottom:16, paddingTop:16, borderTop:`1px solid ${T.cardBorder}` }}>
          <div style={{ fontSize:14, fontWeight:600, color:T.text, marginBottom:4 }}>Payment at booking</div>
          <div style={{ fontSize:12, color:T.textSub, marginBottom:12, lineHeight:1.5 }}>
            Choose whether customers pay when they book online. The amount charged is the package's price shown on the booking page.
          </div>

          {[
            { val:'off', label:'No payment', desc:'Customers pay at drop-off, as normal.' },
            { val:'antiflake', label:'Authorize now, charge later', desc:'Holds the full amount on the card at booking and captures it a few days before drop-off. No charge if they cancel in time — reduces no-shows without eating refund fees. Bookings further out than the capture window are charged in full at booking.' },
            { val:'charge', label:'Charge in full at booking', desc:'Takes full payment immediately when they book. Strongest no-show deterrent; you refund (and absorb the processing fee) if they cancel.' },
          ].map(opt => {
            const active = ((settings.booking || {}).paymentMode || 'off') === opt.val;
            return (
              <label key={opt.val} style={{ display:'flex', gap:10, padding:'10px 12px', marginBottom:8, cursor:'pointer',
                borderRadius:8, border:`1px solid ${active ? '#F4622A' : T.cardBorder}`,
                background: active ? T.inputBg || 'transparent' : 'transparent' }}>
                <input type="radio" name="paymentMode" checked={active}
                  onChange={() => setBooking({ paymentMode: opt.val })}
                  style={{ accentColor:'#F4622A', width:16, height:16, marginTop:2, flexShrink:0 }} />
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color: active ? '#F4622A' : T.text }}>{opt.label}</div>
                  <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5, marginTop:2 }}>{opt.desc}</div>
                </div>
              </label>
            );
          })}

          {((settings.booking || {}).paymentMode) === 'antiflake' && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, paddingLeft:12, flexWrap:'wrap' }}>
              <span style={{ fontSize:13, color:T.text }}>Capture payment</span>
              <input type="number" min="1" max="6"
                value={(settings.booking || {}).captureDayX ?? 5}
                onChange={e => setBooking({ captureDayX: Math.min(6, Math.max(1, parseInt(e.target.value,10) || 5)) })}
                style={{ width:56, height:30, fontSize:13, textAlign:'center',
                  background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:5, color:T.text }} />
              <span style={{ fontSize:13, color:T.textSub }}>days before drop-off (max 6 — card holds expire after 7)</span>
            </div>
          )}
        </div>

        {/* Capacity / lead / window */}
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Max online bookings per day</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }}
            type="number" min="1" max="50" value={(settings.booking || {}).dailyCapacity ?? 3}
            onChange={e => setBooking({ dailyCapacity: parseInt(e.target.value) || 3 })} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>bookings (staff/phone bookings don't count)</span>
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Earliest bookable day</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }}
            type="number" min="0" max="14" value={(settings.booking || {}).leadTimeDays ?? 2}
            onChange={e => setBooking({ leadTimeDays: parseInt(e.target.value) || 0 })} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days from today</span>
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Booking window</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }}
            type="number" min="7" max="90" value={(settings.booking || {}).bookingWindowDays ?? 30}
            onChange={e => setBooking({ bookingWindowDays: parseInt(e.target.value) || 30 })} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days out customers can book</span>
        </div>

        {/* Open days */}
        <div style={{ margin:'14px 0' }}>
          <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:8 }}>Drop-off days</div>
          <div style={{ display:'flex', gap:6 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day, i) => {
              const openDays = (settings.booking || {}).openDays || [2,3,4,5,6];
              const isOpen = openDays.includes(i);
              return (
                <button key={day}
                  onClick={() => setBooking({ openDays: isOpen ? openDays.filter(d => d !== i) : [...openDays, i].sort() })}
                  style={{ fontSize:12, padding:'6px 12px', borderRadius:6, cursor:'pointer', fontFamily:"'Inter',sans-serif",
                    border:`1px solid ${isOpen ? '#F4622A' : T.inputBorder}`,
                    background: isOpen ? '#F4622A22' : 'transparent',
                    color: isOpen ? '#F4622A' : T.textSub,
                    fontWeight: isOpen ? 700 : 400 }}>
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Closed days (3 Nov): one list in Settings › Shop › Closures and
            special hours, for booking, fittings, rentals and the website. */}
        <div style={{ margin:'14px 0' }}>
          <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:6 }}>Closed days</div>
          <div style={{ fontSize:12.5, color:T.textSub, lineHeight:1.6 }}>
            Holidays and other closed days are set once, in Settings › Shop › Closures and special hours (an Administrator's group).
            Online booking, fittings and rentals never offer a day that is closed there.
            {(((settings.closures || []).filter(c => c.kind !== 'hours' && (c.to || c.from) >= new Date().toISOString().slice(0, 10))).length > 0) &&
              ` Coming up: ${(settings.closures || []).filter(c => c.kind !== 'hours' && (c.to || c.from) >= new Date().toISOString().slice(0, 10)).map(c => (c.label || (c.from === (c.to || c.from) ? c.from : `${c.from} to ${c.to}`))).slice(0, 4).join(', ')}.`}
          </div>
        </div>

        {/* Status mapping */}
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Status for new online bookings</span>
          <select value={(settings.booking || {}).onlineBookingStatusId || ''}
            onChange={e => setBooking({ onlineBookingStatusId: e.target.value || null })}
            style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'7px 12px', color:T.text, fontSize:13, outline:'none' }}>
            <option value="">Lightspeed default</option>
            {allStatuses
              .filter(s => {
                // "Done & Paid" is TERMINAL in Lightspeed — it closes the ticket
                // and cannot be undone. Never a valid status for a booking that's
                // just being created or canceled.
                const n = (s.name || '').toLowerCase();
                return !(n.includes('done') && n.includes('paid'));
              })
              .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Status on cancellation</span>
          <select value={(settings.booking || {}).cancellationStatusId || ''}
            onChange={e => setBooking({ cancellationStatusId: e.target.value || null })}
            style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'7px 12px', color:T.text, fontSize:13, outline:'none' }}>
            <option value="">Not set</option>
            {allStatuses
              .filter(s => {
                const n = (s.name || '').toLowerCase();
                return !(n.includes('done') && n.includes('paid'));
              })
              .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Booking page appearance */}
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>Booking page accent color</span>
          <input type="color" value={(settings.booking || {}).accentColor || '#e67e22'}
            onChange={e => setBooking({ accentColor: e.target.value })}
            style={{ width:44, height:30, border:`1px solid ${T.inputBorder}`, borderRadius:6, background:T.input, cursor:'pointer', padding:2 }} />
          <span style={{ ...S.thresholdUnit, color:T.textSub, fontFamily:"'IBM Plex Mono', monospace" }}>{(settings.booking || {}).accentColor || '#e67e22'}</span>
        </div>
        <div style={{ margin:'14px 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
            <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Welcome message</span>
            <span style={{ fontSize:11, color:T.textSub }}>Optional — shown at the top of the booking page</span>
          </div>
          <textarea rows={2} value={(settings.booking || {}).welcomeText || ''}
            placeholder="Welcome! Book your service below and we'll take great care of your bike."
            onChange={e => setBooking({ welcomeText: e.target.value })}
            style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'8px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.5 }} />
        </div>

        {/* Disclaimer */}
        <div style={{ margin:'14px 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
            <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Note on the booking page: service drop-offs</span>
            <span style={{ fontSize:11, color:T.textSub }}>Optional — fittings and rentals have their own notes, below</span>
          </div>
          <textarea rows={2} value={(settings.booking || {}).disclaimerText || ''}
            placeholder="Please do not book e-bike service without calling the shop first to confirm we can service your brand."
            onChange={e => setBooking({ disclaimerText: e.target.value })}
            style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'8px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.5 }} />
        </div>

        {/* Cancellation policy */}
        <div style={{ margin:'14px 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
            <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Cancellation policy</span>
            <span style={{ fontSize:11, color:T.textSub }}>Shown to customers at checkout</span>
          </div>
          <textarea rows={3} value={(settings.booking || {}).cancellationPolicyText || ''}
            placeholder="Cancel up to 24 hours before your drop-off day for a full refund. Same-day cancellations: call the shop."
            onChange={e => setBooking({ cancellationPolicyText: e.target.value })}
            style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'8px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none', resize:'vertical', boxSizing:'border-box', lineHeight:1.5 }} />
        </div>

        {/* Booked service as a ticket line (22 Oct) */}
        <label style={{ display:'flex', alignItems:'flex-start', gap:10, margin:'14px 0', cursor:'pointer' }}>
          <input type="checkbox" checked={!!(settings.booking || {}).addServiceLine}
            onChange={e => setBooking({ addServiceLine: e.target.checked })}
            style={{ accentColor:'#F4622A', width:16, height:16, marginTop:1 }} />
          <span>
            <span style={{ fontSize:13, fontWeight:600, color:T.text, display:'block' }}>Add the booked service to the ticket</span>
            <span style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>The service goes on the Lightspeed ticket as a line at the booked price, so the ticket shows what is owed from the start. Leave this off if your staff add the labor when the bike comes in, or it will be on the ticket twice. "Not sure what it needs?" bookings never get a line.</span>
          </span>
        </label>

        {/* Custom bookings toggle */}
        <label style={{ display:'flex', alignItems:'center', gap:10, margin:'14px 0', cursor:'pointer' }}>
          <input type="checkbox" checked={!!(settings.booking || {}).allowCustomBookings}
            onChange={e => setBooking({ allowCustomBookings: e.target.checked })}
            style={{ accentColor:'#F4622A', width:16, height:16 }} />
          <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Allow "Not sure what it needs?" bookings</span>
          <span style={{ fontSize:12, color:T.textSub }}>Customers describe the problem; you assess and quote before work</span>
          {(settings.booking || {}).allowCustomBookings && (
            <button
              onClick={(e) => {
                e.preventDefault();
                navigator.clipboard.writeText(`${bookingBase}?package=custom`);
                setCopiedLinkId('custom');
                setTimeout(() => setCopiedLinkId(null), 2000);
              }}
              title="Copy the direct booking link for assessment bookings"
              style={{ background:'transparent', border:`1px solid ${copiedLinkId === 'custom' ? '#2ecc71' : T.border}`, borderRadius:6, padding:'3px 10px', fontSize:11, color: copiedLinkId === 'custom' ? '#2ecc71' : T.textSub, cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
              {copiedLinkId === 'custom' ? '✓ Copied' : '🔗 Copy link'}
            </button>
          )}
        </label>

        {/* Enabled packages */}
        <div style={{ marginTop:16, borderTop:`1px solid ${T.border}`, paddingTop:14 }}>
          <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:4 }}>Booking page packages</div>
          <div style={{ fontSize:12, color:T.textSub, marginBottom:10 }}>
            These Lightspeed service items appear on the booking page, in this order. Add a short customer-facing description to each.
          </div>
          <div style={{ display:'grid', gap:8, marginBottom:12 }}>
            {((settings.booking || {}).enabledItems || []).map((pkg, i) => { const appt = pkg.kind === 'appointment'; const setPkg = (patch) => setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).map(e2 => e2.itemID === pkg.itemID ? { ...e2, ...patch } : e2) }); return (
              <div key={pkg.itemID} style={{ background: isDark ? '#1e2a40' : '#f4f4ef', borderRadius:8, padding:'10px 14px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{pkg.name || `Item #${pkg.itemID}`}</span>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span title="Drop-off: the customer picks a day to leave the bike. Appointment: a time with a fitter (set up under Fittings, below)." style={{ display:'inline-flex', border:`1px solid ${T.inputBorder}`, borderRadius:6, overflow:'hidden' }}>
                      {[[false, 'Drop-off'], [true, 'Appointment']].map(([on, label]) => (
                        <button key={label} type="button" aria-pressed={appt === on}
                          onClick={() => setPkg(on ? { kind: 'appointment', minutes: pkg.minutes || 60 } : { kind: undefined })}
                          style={{ fontSize:11, padding:'3px 9px', cursor:'pointer', fontFamily:"'Inter',sans-serif", border:'none', background: appt === on ? '#F4622A' : 'transparent', color: appt === on ? '#fff' : T.textSub, fontWeight: appt === on ? 700 : 400 }}>{label}</button>
                      ))}
                    </span>
                    {appt && (
                      <select value={pkg.minutes || 60} onChange={e => setPkg({ minutes: parseInt(e.target.value, 10) })} title="How long the appointment takes"
                        style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'3px 6px', color:T.text, fontSize:11, fontFamily:'Arial, sans-serif' }}>
                        {APPT_LENGTHS.map(m => <option key={m} value={m}>{m % 60 === 0 ? `${m / 60} hr` : m > 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} min`}</option>)}
                      </select>
                    )}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${bookingBase}?package=${pkg.itemID}`);
                        setCopiedLinkId(pkg.itemID);
                        setTimeout(() => setCopiedLinkId(null), 2000);
                      }}
                      title="Copy the direct booking link for this package — use it on your website's service menu"
                      style={{ background:'transparent', border:`1px solid ${copiedLinkId === pkg.itemID ? '#2ecc71' : T.border}`, borderRadius:6, padding:'3px 10px', fontSize:11, color: copiedLinkId === pkg.itemID ? '#2ecc71' : T.textSub, cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
                      {copiedLinkId === pkg.itemID ? '✓ Copied' : '🔗 Copy link'}
                    </button>
                    <span style={{ fontSize:13, color:T.text, fontFamily:"'IBM Plex Mono', monospace" }}>${(pkg.price || 0).toFixed(2)}</span>
                    <button onClick={() => {
                        const items = [...((settings.booking || {}).enabledItems || [])];
                        if (i > 0) { [items[i-1], items[i]] = [items[i], items[i-1]]; setBooking({ enabledItems: items }); }
                      }}
                      style={{ background:'transparent', border:'none', color:T.textSub, cursor:'pointer', fontSize:13 }}>↑</button>
                    <button onClick={() => {
                        const items = [...((settings.booking || {}).enabledItems || [])];
                        if (i < items.length - 1) { [items[i], items[i+1]] = [items[i+1], items[i]]; setBooking({ enabledItems: items }); }
                      }}
                      style={{ background:'transparent', border:'none', color:T.textSub, cursor:'pointer', fontSize:13 }}>↓</button>
                    <button onClick={() => setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).filter(e2 => e2.itemID !== pkg.itemID) })}
                      style={{ background:'transparent', border:'none', color:'#e74c3c', cursor:'pointer', fontSize:15 }}>×</button>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:6 }}>
                  <input value={pkg.displayName || ''}
                    placeholder="Display name (optional) — e.g. Comp Tune-Up"
                    onChange={e => setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).map(e2 => e2.itemID === pkg.itemID ? { ...e2, displayName: e.target.value } : e2) })}
                    style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box' }} />
                  <input value={pkg.subtitle || ''}
                    placeholder="Subtitle — e.g. Road, hybrid and hardtail"
                    onChange={e => setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).map(e2 => e2.itemID === pkg.itemID ? { ...e2, subtitle: e.target.value } : e2) })}
                    style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box' }} />
                </div>
                <input value={pkg.blurb || ''}
                  placeholder="What's included — e.g. Gear and brake adjustments, wheel true, safety check…"
                  onChange={e => setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).map(e2 => e2.itemID === pkg.itemID ? { ...e2, blurb: e.target.value } : e2) })}
                  style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box', marginBottom:6 }} />
                {!appt && <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:6 }}>
                  <input value={pkg.turnaround || ''}
                    placeholder="Turnaround text — e.g. 2–3 day turnaround"
                    onChange={e => setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).map(e2 => e2.itemID === pkg.itemID ? { ...e2, turnaround: e.target.value } : e2) })}
                    style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box' }} />
                  <input value={pkg.turnaroundDays ?? ''}
                    placeholder="Est. days (#)"
                    type="text" inputMode="numeric"
                    onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setBooking({ enabledItems: ((settings.booking || {}).enabledItems || []).map(e2 => e2.itemID === pkg.itemID ? { ...e2, turnaroundDays: v ? parseInt(v) : null } : e2) }); }}
                    style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box' }} />
                </div>}
              </div>
            ); })}
            {((settings.booking || {}).enabledItems || []).length === 0 && (
              <div style={{ fontSize:12, color:'#e67e22' }}>No packages enabled — the booking page will show no services.</div>
            )}
          </div>

          {/* Browse Lightspeed service items */}
          <button
            onClick={() => bookingItems.length ? setBookingItems([]) : loadBookingItems()}
            style={{ ...S.btn(false), color:'#3498db', borderColor:'#3498db33', fontSize:12, marginBottom:8 }}>
            {loadingBookingItems ? 'Loading…' : bookingItems.length ? 'Hide Lightspeed items' : '↓ Add from Lightspeed'}
          </button>
          {bookingItems.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
              <div style={{ position:'relative', width:280 }}>
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:12, color:T.textSub, pointerEvents:'none' }}>🔍</span>
                <input
                  autoFocus
                  value={bookingItemFilter}
                  onChange={e => setBookingItemFilter(e.target.value)}
                  placeholder="Filter items…"
                  style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:20, padding:'6px 30px 6px 30px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', outline:'none', boxSizing:'border-box' }}
                />
                {bookingItemFilter && (
                  <span
                    onClick={() => setBookingItemFilter('')}
                    style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:13, color:T.textSub, cursor:'pointer', fontWeight:700, lineHeight:1 }}>
                    ×
                  </span>
                )}
              </div>
              {bookingItemFilter && (
                <span style={{ fontSize:11, color:T.textSub }}>
                  {bookingItems.filter(item => {
                    const q = bookingItemFilter.trim().toLowerCase();
                    return (item.name || '').toLowerCase().includes(q) || (item.category || '').toLowerCase().includes(q);
                  }).length} matching
                </span>
              )}
            </div>
          )}
          {bookingItems.length > 0 && (
            <div style={{ display:'grid', gap:6, maxHeight:240, overflowY:'auto' }}>
              {bookingItems
                .filter(item => {
                  const q = bookingItemFilter.trim().toLowerCase();
                  if (!q) return true;
                  return (item.name || '').toLowerCase().includes(q) || (item.category || '').toLowerCase().includes(q);
                })
                .map(item => {
                const already = ((settings.booking || {}).enabledItems || []).some(e2 => String(e2.itemID) === String(item.id));
                return (
                  <div key={item.id}
                    onClick={() => {
                      if (already) return;
                      const items = (settings.booking || {}).enabledItems || [];
                      setBooking({ enabledItems: [...items, { itemID: item.id, name: item.name, price: item.price || 0, blurb: '' }] });
                    }}
                    style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:8, padding:'8px 12px',
                      cursor: already ? 'default' : 'pointer', opacity: already ? 0.5 : 1,
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}
                    onMouseEnter={e => { if (!already) e.currentTarget.style.borderColor = '#F4622A'; }}
                    onMouseLeave={e => e.currentTarget.style.borderColor = T.cardBorder}>
                    <div>
                      <span style={{ fontSize:13, color:T.text }}>{item.name}</span>
                      {item.category && <span style={{ fontSize:11, color:T.textSub, marginLeft:8 }}>{item.category}</span>}
                    </div>
                    <span style={{ fontSize:12, color:T.textSub, fontFamily:"'IBM Plex Mono', monospace" }}>
                      {already ? 'Added' : (item.price != null ? `$${Number(item.price).toFixed(2)}` : '')}
                    </span>
                  </div>
                );
                })}
            </div>
          )}
          <div style={{ fontSize:11, color:T.textSub, marginTop:10, fontStyle:'italic' }}>
            ⓘ Packages come from your Lightspeed catalog so tickets and sales reports stay accurate. Want a booking-only special? Create the item in Lightspeed first, then add it here.
          </div>
        </div>
      </div>

      <SettingsFittings />
      <SettingsRentals />
    </>
  );
}
